import { isValidElement, ReactElement, ReactNode, useEffect, useState } from 'react';

interface TypewriterEffectProps {
    speed?: number;
    cursor?: boolean;
    children: ReactNode;
}

function getChildrenText(children: ReactNode): string {
    // Handle null, undefined, or empty children
    if (!children) return '';

    // If children is a string or number, return it directly
    if (typeof children === 'string' || typeof children === 'number') {
        return String(children);
    }

    // If children is an array, process each child
    if (Array.isArray(children)) {
        return children.map(child => getChildrenText(child)).join('');
    }

    // If children is a React element, extract text from its children
    if (isValidElement(children)) {
        return getChildrenText((children as ReactElement<{ children?: ReactNode }>).props.children);
    }

    // For any other type (boolean, function, etc.), return empty string
    return '';
}

export function TypewriterEffect({ speed = 100, cursor = true, children }: TypewriterEffectProps) {
    const [thisText, setThisText] = useState("");
    const [displayedText, setDisplayedText] = useState("");
    const [index, setIndex] = useState(0);
    const [typing, setTyping] = useState(true);

    useEffect(() => {
        setThisText(getChildrenText(children));
    }, [children]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (index < thisText.length) {
                setDisplayedText((prev) => prev + thisText[index]);
                setIndex(index + 1);
            } else {
                clearInterval(interval);
                setTyping(false);
            }
        }, speed);

        return () => clearInterval(interval);
    }, [index, thisText, speed]);

    return (
        <span className="typewriter-text">
            {typing ? displayedText : children}
            {cursor && <span className="typewriter-cursor">_</span>}
        </span>
    );
}

export function TypewriterCursor() {
    return <span className="typewriter-cursor">_</span>;
}